import React from 'react';
import { useApi } from '../hooks/useApi';
import { api } from '../api';
import { fmt, fmtDate, extractCategory } from '../utils';
import { Card, CardHeader, StatCard, StatGrid, Badge, Tag, Skeleton, PageWrap, PageHeader, Table, useIsMobile } from '../components/UI';
import '../styles/Statutory.css';

export default function Statutory({ sourceId, syncRunId }) {
  const { data, loading } = useApi(() => api.statutory(sourceId, syncRunId), [sourceId, syncRunId]);
  const isMobile = useIsMobile();

  const findings = (data?.findings || []).slice().sort((a, b) =>
    (b.months_overdue ?? 0) - (a.months_overdue ?? 0) ||
    Math.abs(parseFloat(b.balance ?? 0)) - Math.abs(parseFloat(a.balance ?? 0))
  );

  const summary       = data?.summary || [];
  const outstanding   = data?.outstandingCount ?? 0;
  const allClear      = data?.allClear ?? true;
  const criticalCount = findings.filter(f => f.urgency === 'critical').length;
  const maxMonths     = Math.max(0, ...findings.map(f => f.months_overdue ?? 0));

  const getLedgerDescription = (name) => {
    if (!name) return '';
    const n = name.toLowerCase();
    if (n.includes('employee share')) return 'Deducted from employee salary — to be remitted';
    if (n.includes('employer share')) return 'Company contribution — to be remitted';
    if (n.includes('admin charges'))  return 'Administrative charges on EPF — to be remitted';
    if (n.includes('payable'))        return 'Net amount due to government';
    return '';
  };

  const getLedgerNature = (name) => {
    if (!name) return null;
    const n = name.toLowerCase();
    if (n.includes('employee share')) return 'Employee';
    if (n.includes('employer share')) return 'Employer';
    if (n.includes('admin charges'))  return 'Admin';
    if (n.includes('payable'))        return 'Payable';
    if (n.includes('omett') || n.includes('omit')) return 'Omitted';
    return null;
  };

  const getNatureBadgeVariant = (nature) => {
    if (nature === 'Employee') return 'blue';
    if (nature === 'Employer') return 'green';
    if (nature === 'Admin')    return 'purple';
    if (nature === 'Payable')  return 'amber';
    if (nature === 'Omitted')  return 'red';
    return 'gray';
  };

  const getUrgencyVariant = (urgency) => {
    if (urgency === 'critical') return 'red';
    if (urgency === 'warning')  return 'amber';
    return 'gray';
  };

  const getSummaryAccent = (months) => {
    if (months >= 3) return 'red';
    if (months >= 1) return 'amber';
    return 'green';
  };

  return (
    <PageWrap>
      <PageHeader title="Statutory Compliance" sub="EPF · ESI · TDS · Professional Tax · Labour Welfare Fund · PSDT" />

      <StatGrid cols={isMobile ? 2 : 4}>
        <StatCard label="Overall Status"       value={allClear ? 'ALL CLEAR' : 'ACTION NEEDED'} accent={allClear ? 'green' : 'red'} />
        <StatCard label="Outstanding Ledgers"  value={outstanding}   accent={outstanding > 0 ? 'red' : 'green'} />
        <StatCard label="Critical"             value={criticalCount} accent={criticalCount > 0 ? 'red' : 'green'} />
        <StatCard label="Max Months Overdue"   value={maxMonths > 0 ? `${maxMonths}m` : '—'} accent={maxMonths >= 3 ? 'red' : maxMonths >= 1 ? 'amber' : 'green'} />
      </StatGrid>

      {summary.length > 0 && (
        <div className="st-summary-grid">
          {summary.map(s => {
            const accent = getSummaryAccent(s.months_overdue);
            const color = accent === 'red' ? 'var(--red)' : accent === 'amber' ? 'var(--amber)' : 'var(--green)';
            return (
              <div
                key={s.type}
                className="st-summary-card"
                style={{
                  border: `1px solid ${color}`,
                  minWidth: isMobile ? 'calc(50% - 6px)' : 150,
                }}
              >
                <div className="st-summary-type" style={{ color }}>{s.type}</div>
                <div className="st-summary-amount" style={{ fontSize: isMobile ? 18 : 22 }}>
                  ₹{fmt(s.total_balance)}
                </div>
                <div className="st-badge-wrap">
                  <Badge
                    text={s.months_overdue > 0 ? `${s.months_overdue}m overdue` : 'Current'}
                    variant={accent === 'red' ? 'red' : accent === 'amber' ? 'amber' : 'green'}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Card>
        <CardHeader title="Statutory Ledger Details" />
        {loading ? <Skeleton rows={8} /> : (
          <Table
            columns={[
              { label: 'Category', key: 'message',
                render: r => <strong>{extractCategory(r.message, (r.ledger_name||'').split(' ')[0])}</strong> },
              { label: 'Ledger Name', key: 'ledger_name',
                render: r => (
                  <div>
                    <div className="st-ledger-name">{r.ledger_name || '—'}</div>
                    {getLedgerDescription(r.ledger_name) && (
                      <div className="st-ledger-desc">{getLedgerDescription(r.ledger_name)}</div>
                    )}
                  </div>
                )},
              { label: 'Nature', key: 'ledger_nature',
                render: r => {
                  const nature = getLedgerNature(r.ledger_name);
                  return nature
                    ? <Badge text={nature} variant={getNatureBadgeVariant(nature)} />
                    : <span className="st-dim">—</span>;
                }},
              { label: 'Balance', key: 'balance', right: true, mono: true,
                render: r => {
                  const b = parseFloat(r.balance ?? 0);
                  return (
                    <span
                      className="st-mono-cell"
                      style={{ color: Math.abs(b) > 0.01 ? 'var(--red)' : 'var(--green)' }}
                    >
                      ₹{fmt(Math.abs(b))}
                    </span>
                  );
                }},
              { label: 'Last Booked', key: 'last_transaction_date', noWrap: true,
                render: r => r.last_transaction_date
                  ? <span className="st-date-cell">{fmtDate(r.last_transaction_date)}</span>
                  : <span className="st-dim">—</span> },
              { label: 'Due Date', key: 'due_date', noWrap: true,
                render: r => r.due_date
                  ? <span className={`st-date-cell${r.overdue_since ? ' st-date-cell--overdue' : ''}`}>
                      {fmtDate(r.due_date)}
                    </span>
                  : <span className="st-dim">—</span> },
              { label: 'Months Overdue', key: 'months_overdue', right: true,
                render: r => {
                  const m = r.months_overdue ?? 0;
                  return m === 0
                    ? <Badge text="Current" variant="green" />
                    : <Badge text={`${m}m overdue`} variant={getUrgencyVariant(r.urgency)} />;
                }},
              { label: 'Urgency', key: 'urgency',
                render: r => <Badge text={(r.urgency||'info').toUpperCase()} variant={getUrgencyVariant(r.urgency)} /> },
              { label: 'Status', key: 'balance_status',
                render: r => {
                  const out = Math.abs(parseFloat(r.balance ?? 0)) > 0.01;
                  return <Badge text={out ? 'OUTSTANDING' : 'CLEAR'} variant={out ? 'red' : 'green'} />;
                }},
            ]}
            rows={findings}
            emptyMsg="No statutory ledgers found"
          />
        )}
      </Card>

      {!allClear && (
        <div className="st-callout-red">
          <span className="st-callout-icon">⚠</span>
          <div className="st-callout-text">
            <strong>Action Required:</strong> Statutory dues must be paid by the 15th of the following month. Late payment attracts interest at 12% p.a. for EPF and damages under the ESI Act.
          </div>
        </div>
      )}
    </PageWrap>
  );
}
